import React, { useState, useRef, useEffect } from 'react';
import { FaRegStopCircle } from "react-icons/fa";
import { FaArrowUp, FaArrowCircleRight, FaArrowAltCircleUp } from "react-icons/fa";
import { ArrowUp } from "lucide-react";
import { FaArrowCircleUp } from "react-icons/fa"; 
import { FaCircleArrowUp } from "react-icons/fa6";
import { ChatInputProps } from '../../types/types';
import { lightenColor } from "../../constants/lightenColor";
import { isLightColor } from "../../utils/LuminosityChecker";
import { darkenColor } from "../../constants/darkenColor";

const ChatInput: React.FC<ChatInputProps & {themeOverride?: 'light' | 'dark' | 'auto'}> = ({
  input,
  setInput,
  onSendMessage,
  configData,
  loading,
  pendingMsgCount,
  allowSendMsg,
  messages,
  activeChatId,
  isSidebarOpen,
  themeOverride = 'auto'
}) => {
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const [animate, setAnimate] = useState(false);
  const [linkRequestModal, setIsLinkRequestModalOpen] = useState(false);
  const [isMobileState, setIsMobileState] = useState(false);

  const app_ai_settings = configData && configData?.app_ai_settings;

  const [prompts, setPrompts] = useState(() => {
    const {
      ai_suggestedprompt1,
      ai_suggestedprompt2,
      ai_suggestedprompt3,
      ai_suggestedprompt4,
    } = configData.app_ai_settings;

    const promptList = [
      ai_suggestedprompt1,
      ai_suggestedprompt2,
      ai_suggestedprompt3,
      ai_suggestedprompt4,
    ].filter((p) => p && p.trim() !== "");

    return promptList;
  });

  // Use configData colors as default with theme override
  const backgroundColor = configData.app_ui_settings.ui_backgroundcolor;
  const configIsLight = isLightColor(backgroundColor);
  
  // Determine effective theme based on themeOverride
  const effectiveIsLight = themeOverride === 'auto' ? configIsLight : themeOverride === 'light';
  
  // Use theme-aware colors
  const chatBackground = themeOverride === 'auto' 
    ? backgroundColor || (configIsLight ? '#ffffff' : '#212121')
    : (effectiveIsLight ? '#ffffff' : '#212121');
    
  // ChatGPT-style input background - subtle variation of config color
  const createInputVariation = (baseColor: string, isLight: boolean) => {
    if (!baseColor || !baseColor.startsWith("#")) return baseColor;
    
    const hex = baseColor.slice(1);
    const num = parseInt(hex, 16);
    const r = (num >> 16) & 255;
    const g = (num >> 8) & 255;
    const b = num & 255;
    
    // More noticeable but still subtle adjustment for input background
    const adjustment = isLight ? -8 : 8; // Darker for light themes, lighter for dark themes
    const newR = Math.max(0, Math.min(255, r + adjustment));
    const newG = Math.max(0, Math.min(255, g + adjustment));
    const newB = Math.max(0, Math.min(255, b + adjustment));
    
    return `rgb(${newR}, ${newG}, ${newB})`;
  };

  const inputBackground = themeOverride === 'auto'
    ? backgroundColor // Same as background for auto theme
    : themeOverride === 'light' 
    ? backgroundColor // Same as background for light theme
    : '#2d2d30';

  const promptBackground = themeOverride === 'auto'
    ? createInputVariation(backgroundColor, configIsLight) // Slight variation for auto theme
    : themeOverride === 'light' 
    ? createInputVariation(backgroundColor, true) // Slight variation for light theme
    : '#2d2d30'; 
    
  const inputBorderColor = themeOverride === 'auto'
    ? (configIsLight ? darkenColor(backgroundColor, 8) : lightenColor(backgroundColor, 8))
    : (effectiveIsLight ? '#e5e5e5' : '#404040');
    
  // ChatGPT-style button colors
  const buttonEnabledBg = effectiveIsLight ? '#000000' : '#ffffff';
  const buttonEnabledColor = effectiveIsLight ? '#ffffff' : '#000000';
  const buttonDisabledBg = effectiveIsLight ? '#e5e7eb' : '#374151';
  const buttonDisabledColor = effectiveIsLight ? '#9ca3af' : '#6b7280';

  const lighterBackground = themeOverride === 'auto'
    ? (configIsLight ? lightenColor(backgroundColor, 10) : lightenColor(backgroundColor, 20))
    : (effectiveIsLight ? '#f7f7f8' : '#2d2d30');

  const lighterInputBoxBackground = inputBackground;
  
  const themeTextColor = themeOverride === 'auto'
    ? configData.app_ui_settings.ui_fontcolor
    : (effectiveIsLight ? '#000000' : '#ffffff');

  // Handle responsive behavior
  useEffect(() => {
    const handleResize = () => {
      setIsMobileState(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (app_ai_settings.ai_showsuggestedprompts === "true") {
      setAnimate(true);
    }
  }, []);

  useEffect(() => {
  if (messages.length === 0) {
    // Focus the textarea for new chat
    inputRef.current?.focus();
  }
}, [messages]);

  useEffect(() => {
    setAnimate(false);
    const timeout = setTimeout(() => {
      setAnimate(true);
    }, 50);
    return () => clearTimeout(timeout);
  }, [messages]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter") {
      if (pendingMsgCount <= 0) {
        e.preventDefault();
        return;
      }
      if (e.shiftKey) {
        e.preventDefault();
        const textArea = e.currentTarget;
        const { selectionStart, selectionEnd } = textArea;

        const newText =
          input.substring(0, selectionStart) +
          "\n" +
          input.substring(selectionEnd);

        setInput(newText);

        setTimeout(() => {
          textArea.selectionStart = textArea.selectionEnd = selectionStart + 1;
        }, 0);
      } else {
        e.preventDefault();
        onSendMessage(input);
        if (inputRef.current) {
          inputRef.current.style.height = 'auto';
        }
      }
    }
  };

  const handlePromptClick = (prompt: string) => {
    setInput(prompt);
    inputRef.current?.focus();
    onSendMessage(prompt);
  };


  return (
    <div
      className={`flex flex-col w-full transition-all duration-300  ${
        messages.length === 0
          ? "mx-auto px-4 max-w-6xl" // Wider like ChatGPT but not unlimited
          : "fixed bottom-0 z-10 px-4 pb-2"
      }`}
      style={{ 
        backgroundColor: messages.length > 0 ? chatBackground : 'transparent',
        left: messages.length > 0 ? (isSidebarOpen && !isMobileState ? '256px' : (!isMobileState ? '52px' : '0')) : 'auto',
        right: messages.length > 0 ? '0' : 'auto',
        maxWidth: messages.length > 0 ? 'none' : '720px',
        width: messages.length > 0 ? (isSidebarOpen && !isMobileState ? 'calc(100vw - 256px)' : '100vw') : '100%'
      }}
    >
      <div className={messages.length > 0 ? "w-full max-w-4xl mx-auto" : ""}>
        {String(allowSendMsg) === "false" && (
          <div 
            className="w-full text-center py-3 px-4 rounded-lg flex items-center justify-center"
            style={{ 
              background: lighterInputBoxBackground,
              fontWeight: "500",
              fontSize: "14px",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
            }}
          >
            <span className="text-red-500">Maximum permitted message quota exceeded for this month please contact course owner to increase the quota</span>
            {linkRequestModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white p-6 rounded-lg shadow-lg max-w-sm w-full">
                  <h2 className="text-lg font-semibold mb-4">Coming Soon</h2>
                  <p>This feature will be available shortly. Stay tuned!</p>
                  <button
                    className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                    onClick={() => setIsLinkRequestModalOpen(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        <div className={`relative ${
          messages.length === 0 
            ? 'w-full mx-auto' // Full width like ChatGPT when no messages
            : 'w-full max-w-4xl mx-auto' // Standard width when messages present
        }`}>
               <textarea
                  ref={inputRef}
                  className="w-full rounded-full focus:outline-none resize-none overflow-hidden transition-all duration-200 placeholder:text-gray-500 placeholder:font-normal placeholder:text-base placeholder:font-inter"
                  value={input}
                  style={{
                  color: themeTextColor,
                  background: inputBackground,
                  border: `1px solid ${inputBorderColor}`,
                  lineHeight: '20px', // Tighter line height for better fit
                  height: '54px', // Fixed height like ChatGPT (approximately 1.3cm)
                  minHeight: '54px',
                  maxHeight: '200px',
                  fontSize: '16px',
                  paddingTop: '14px', // Slightly higher for placeholder positioning
                  paddingBottom: '18px', // Compensate to maintain overall centering
                  paddingLeft: '16px',
                  paddingRight: '70px', // More space for larger send button
                  boxShadow:
                    themeOverride === 'auto'
                      ? configIsLight
                        ? '0 2px 6px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.06)'
                        : '0 2px 6px rgba(0, 0, 0, 0.2), 0 1px 2px rgba(0, 0, 0, 0.15)'
                      : effectiveIsLight
                      ? '0 2px 6px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.06)'
                      : '0 2px 6px rgba(0, 0, 0, 0.2), 0 1px 2px rgba(0, 0, 0, 0.15)',
                  whiteSpace: 'pre-wrap',
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                  overflowY: 'hidden',  // allow vertical scrolling if needed
                  overflowX: 'hidden', // hide horizontal scroll
                }}

                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ask anything"
                  onKeyDown={handleKeyDown}
                  onInput={(e) => {
                    const target = e.target as HTMLTextAreaElement;
                    if (target.value === '') {
                      target.style.height = "54px"; // reset to fixed height when empty
                    } else {
                      // Reset height to measure actual content height
                      target.style.height = "54px";
                      
                      // Only expand if content actually needs more than one line
                      if (target.scrollHeight > 54) {
                        target.style.height = `${Math.min(target.scrollHeight, 200)}px`;
                      }
                    }
                  }}
                />

        
                <button
                  onClick={() => {
                    if (loading) {
                      // Handle stop functionality here if needed
                    } else if (pendingMsgCount > 0) {
                      onSendMessage(input);
                    }
                  }} 
                   className={`absolute right-2 top-1/2 transform -translate-y-1/2 ${
                    (input.trim() === "" || pendingMsgCount <= 0) && !loading ? "cursor-not-allowed opacity-50" : "cursor-pointer"} p-2`}
                  disabled={input.trim() === "" && !loading}
                  style={{
                    display: 'inline-block',
                    paddingTop: isMobileState ? '6px' : '3px', // Responsive paddingTop
                    borderRadius: '50%',
                  }}
                >
                  {loading ? (
                    <FaRegStopCircle
                      className="h-9 w-9 opacity-50 cursor-not-allowed"
                      style={{ color: buttonEnabledBg, fontSize: '28px' }}
                    />
                  ) : (
                    <FaCircleArrowUp
                    className="h-8 w-8"
                      style={{ color: buttonEnabledBg }}
                    />
                  )}
                </button>   
              </div>

        {String(app_ai_settings.ai_showsuggestedprompts) === "true" && messages.length === 0 && String(allowSendMsg) === "true" && (
        <div
          className={`grid grid-cols-1 sm:grid-cols-2 gap-6 w-full mt-10 ${animate ? 'fadeInAnimation' : ''}`}
          style={{
            maxWidth: '100%',
            alignItems: 'stretch',
          }}
        >
          {prompts.map((prompt, index) => (
            <div
              key={index}
              className="w-full"
              style={{
                minHeight: '80px',
                maxHeight: '120px',
              }}
            >
              <button
                onClick={() => handlePromptClick(prompt)}
                className={`text-black-600 rounded-[1.5rem] flex items-center justify-center px-4 py-4 w-full h-full transition-all duration-300 ease-in-out transform hover:translate-y-[-2px] hover:shadow-lg  ml-0`}
                style={{
                  color: themeTextColor,
                  background: promptBackground,
                  fontSize: '14px',
                  fontWeight: '400',
                  textAlign: 'center',
                  lineHeight: '1.4',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: '80px',
                  maxHeight: '120px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  wordWrap: 'break-word',
                  hyphens: 'auto',
                  
                }}
              >
                <span className="text-center leading-tight">
                  {prompt}
                </span>
              </button>
            </div>
          ))}
        
          
        
        </div>
        )}

      </div>
    </div>
  );
};

export default ChatInput;